import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronRight, Coins, Clock, Layers } from "lucide-react";
import api from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { TypeIcon, StatusPill } from "./workMeta";
import { fmtH, fmtMoney } from "./WorkLog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";

const pct = (a, b) => (b > 0 ? Math.min(100, Math.round((a / b) * 100)) : 0);

export function Burn({ value, total, testId, tone = "#1E3A8A" }) {
  const p = pct(value, total);
  return (
    <div className="flex items-center gap-2 min-w-[110px]" data-testid={testId} title={`${p}%`}>
      <div className="h-1.5 flex-1 rounded-full bg-secondary overflow-hidden"><div className="h-full transition-[width]" style={{ width: `${p}%`, background: tone }} /></div>
      <span className="text-[10px] text-muted-foreground w-8 text-right">{p}%</span>
    </div>
  );
}

export function SprintCost({ stats, currency, id, showDelivered = true }) {
  const { t } = useLang();
  if (!stats) return null;
  const over = stats.estimate_hours > 0 && stats.logged_hours > stats.estimate_hours;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5" data-testid={`sprint-cost-${id}`}>
      <span className={`inline-flex items-center gap-1 text-[11px] rounded border px-1.5 py-0.5 ${over ? "text-[#991B1B] bg-[#FEE2E2] border-[#FECACA]" : "text-slate-600 bg-white"}`} title={`${t("loggedShort")} / ${t("estimateShort")} · ${fmtH(stats.remaining_hours)} ${t("remainingShort")}`} data-testid={`sprint-hours-${id}`}>
        <Clock className="h-3 w-3" />{fmtH(stats.logged_hours)} / {fmtH(stats.estimate_hours)}
      </span>
      {stats.price != null && stats.price > 0 && (
        <span className="inline-flex items-center gap-1 text-[11px] rounded border px-1.5 py-0.5 text-[#065F46] bg-[#D1FAE5] border-[#A7F3D0]" title={`${t("delivered")}: ${fmtMoney(stats.done_price, currency)} · ${stats.priced_count} ${t("pricedItems")}`} data-testid={`sprint-price-${id}`}>
          <Coins className="h-3 w-3" />{fmtMoney(stats.price, currency)}
          {showDelivered && <span className="opacity-70">· {pct(stats.done_price, stats.price)}% {t("delivered").toLowerCase()}</span>}
        </span>
      )}
    </span>
  );
}

export default function EpicRollup({ projectId, refreshKey }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(() => localStorage.getItem("apha_epic_rollup") !== "0");
  useEffect(() => {
    api.get(`/projects/${projectId}/epics/rollup`).then((r) => setData(r.data)).catch(() => setData({ epics: [], totals: {} }));
  }, [projectId, refreshKey]);
  const toggle = () => { localStorage.setItem("apha_epic_rollup", open ? "0" : "1"); setOpen(!open); };
  if (!data || (data.epics.length === 0 && !data.no_epic)) return null;
  const { epics, no_epic, totals, can_see_price, currency } = data;
  const rows = [...epics, ...(no_epic ? [{ ...no_epic, id: "none", key: null, title: t("noEpic") }] : [])];
  const cell = (r, testId) => (
    <TableRow key={r.id} className={r.key ? "cursor-pointer" : "text-muted-foreground"} data-testid={testId} onClick={() => r.key && navigate(`/work/${r.key}`)}>
      <TableCell className="max-w-[280px]">
        <div className="flex items-center gap-2 min-w-0">
          {r.key ? <TypeIcon type="EPIC" /> : <Layers className="h-3.5 w-3.5 text-muted-foreground" />}
          {r.key && <span className="text-xs text-muted-foreground whitespace-nowrap">{r.key}</span>}
          <span className="truncate text-sm">{r.title}</span>
          {r.key && r.status_name && <StatusPill name={r.status_name} category={r.status_category} />}
        </div>
      </TableCell>
      <TableCell><div className="flex items-center gap-2"><Burn value={r.done_count} total={r.item_count} testId={`epic-progress-${r.id}`} /><span className="text-[11px] text-muted-foreground whitespace-nowrap">{r.done_count}/{r.item_count}</span></div></TableCell>
      <TableCell className="text-xs whitespace-nowrap text-right">{r.done_points}/{r.points}</TableCell>
      <TableCell className={`text-xs whitespace-nowrap text-right ${r.estimate_hours > 0 && r.logged_hours > r.estimate_hours ? "text-[#991B1B] font-medium" : ""}`} data-testid={`epic-hours-${r.id}`}>{fmtH(r.logged_hours)} / {fmtH(r.estimate_hours)}<span className="text-muted-foreground"> · {fmtH(r.remaining_hours)} {t("remainingShort")}</span></TableCell>
      {can_see_price && <TableCell className="text-xs whitespace-nowrap text-right" data-testid={`epic-price-${r.id}`}>{r.price > 0 ? fmtMoney(r.price, currency) : "—"}</TableCell>}
      {can_see_price && <TableCell data-testid={`epic-burn-${r.id}`}>{r.price > 0 ? <div className="flex items-center gap-2 justify-end"><span className="text-xs whitespace-nowrap">{fmtMoney(r.done_price, currency)}</span><Burn value={r.done_price} total={r.price} tone="#059669" /></div> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>}
    </TableRow>
  );
  return (
    <section className="apha-card" data-testid="epic-rollup">
      <button type="button" onClick={toggle} data-testid="epic-rollup-toggle" className="w-full px-4 py-2.5 flex flex-wrap items-center gap-2 text-left border-b">
        {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        <span className="font-heading font-semibold">{t("epicRollup")}</span>
        <span className="text-xs text-muted-foreground">{epics.length} {t("epics").toLowerCase()}</span>
        <span className="ml-auto inline-flex flex-wrap items-center gap-1.5 text-[11px]" data-testid="epic-rollup-totals">
          <span className="rounded border bg-white px-1.5 py-0.5 inline-flex items-center gap-1"><Clock className="h-3 w-3" />{fmtH(totals.logged_hours)} / {fmtH(totals.estimate_hours)}</span>
          {can_see_price && totals.price > 0 && <span className="rounded border px-1.5 py-0.5 inline-flex items-center gap-1 text-[#065F46] bg-[#D1FAE5] border-[#A7F3D0]"><Coins className="h-3 w-3" />{fmtMoney(totals.price, currency)} <span className="opacity-70">· {pct(totals.done_price, totals.price)}% {t("delivered").toLowerCase()}</span></span>}
        </span>
      </button>
      {open && (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>{t("epic")}</TableHead><TableHead>{t("progress")}</TableHead><TableHead className="text-right">{t("points")}</TableHead>
              <TableHead className="text-right capitalize">{t("loggedShort")} / {t("estimateShort")}</TableHead>
              {can_see_price && <TableHead className="text-right">{t("price")}</TableHead>}{can_see_price && <TableHead className="text-right">{t("budgetBurn")}</TableHead>}
            </TableRow></TableHeader>
            <TableBody>{rows.map((r) => cell(r, `epic-row-${r.key || "none"}`))}</TableBody>
          </Table>
        </div>
      )}
    </section>
  );
}
